import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PlayIcon from "../../../../assets/icons/jouer.svg";
import SelectedIcon from "../../../../assets/icons/selected.svg";
import UnselectIcon from "../../../../assets/icons/unselect.svg";
import ChevronBackIcon from "../../../../assets/icons/chevron-back-white.svg";
import CloseIcon from "../../../../assets/icons/close.svg";
import Loader from "../../../loader/Loader";
import VideoPlayer from "../../../VideoPlayerComponent/VideoPlayer";
import { AppDispatch, StoreState } from "../../../../redux/store";
import { getQuickFileAlbumDetails } from "../../../../redux/quickFile/quickFileSlice";
import { IThumbnail } from "../../../../helpers/types";
import { FALLBACK_IMAGE, THUMBNAIL_TYPES } from "../../../../helpers/constant";
import VoiceChatListRow from "../../../VoiceChatListRow/VoiceChatListRow";
import { getVideoDurationFromUrl } from "../../../../utls/FunctionsUtil";
import "./SelectQuickFileMediaModal.less";

export type QuickFileMedia = { id: string; url: string; thumbnail?: string; isVideo?: boolean; isAudio?: boolean };
export type SelectQuickFileMediaModalProps = {
  albumId: string;
  type: "media" | "audio";
  onClose: () => void;
  onConfirm: (files: QuickFileMedia[]) => void;
  maxSelection?: number;
  onBack?: () => void;
};

const SelectQuickFileMediaModal: React.FC<SelectQuickFileMediaModalProps> = ({ albumId, type, onClose, onConfirm, maxSelection, onBack }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const album = useSelector((state: StoreState) => state.quickFile.item);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [isPreviewVideo, setIsPreviewVideo] = useState<boolean>(false);
  const [videoDurations, setVideoDurations] = useState<{ [fileId: string]: string }>({});

  useEffect(() => {
    if (albumId) {
      setLoading(true);
      dispatch(getQuickFileAlbumDetails(albumId));
    }
  }, [albumId, dispatch]);

  useEffect(() => {
    if (album?.files) {
      setLoading(false);
    }
  }, [album]);

  useEffect(() => {
    const fetchDurations = async () => {
      if (!album?.files) return;
      const durations: { [fileId: string]: string } = {};
      await Promise.all(
        album.files.map(async (m: any) => {
          const isVideo =
            (m.mimeType as string)?.startsWith("video/") ||
            (m.url as string)?.includes(".mp4") ||
            (m.url as string)?.includes(".webm") ||
            (m.url as string)?.includes(".mov");
          if (isVideo) {
            durations[m.fileId as string] = await getVideoDurationFromUrl(m.url as string);
          }
        })
      );
      setVideoDurations(durations);
    };
    fetchDurations();
  }, [album?.files]);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const media = useMemo(() => {
    const files = album?.files || [];
    if (type === "audio") {
      return files.filter((file: any) => {
        const mimeType = file.mimeType as string;
        const url = file.url as string;
        return (
          mimeType?.startsWith("audio/") ||
          url?.includes(".mp3") ||
          url?.includes(".wav") ||
          url?.includes(".m4a") ||
          url?.includes(".aac") ||
          url?.includes(".webm") ||
          url?.includes(".ogg")
        );
      });
    }
    return files.filter((file: any) => {
      const mimeType = file.mimeType as string;
      const url = file.url as string;
      return (
        mimeType?.startsWith("image/") ||
        mimeType?.startsWith("video/") ||
        url?.includes(".mp4") ||
        url?.includes(".webm") ||
        url?.includes(".mov") ||
        url?.includes(".avi") ||
        url?.includes(".jpg") ||
        url?.includes(".jpeg") ||
        url?.includes(".png") ||
        url?.includes(".gif") ||
        url?.includes(".webp") ||
        url?.includes(".avif") ||
        url?.includes(".bmp") ||
        url?.includes(".svg")
      );
    });
  }, [album?.files, type]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (maxSelection === 1) {
          next.clear();
          next.add(id);
        } else if (maxSelection && next.size >= maxSelection) {
          return prev;
        } else {
          next.add(id);
        }
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = media
      .filter((m: any) => selectedIds.has(m.fileId))
      .map((m: any) => ({
        id: m.fileId as string,
        url: m.url as string,
        isVideo:
          (m.mimeType as string)?.startsWith("video/") ||
          (m.url as string)?.includes(".mp4") ||
          (m.url as string)?.includes(".webm") ||
          (m.url as string)?.includes(".mov"),
        isAudio: type === "audio",
        thumbnail: (m.thumbnails as IThumbnail[] | undefined)?.find((th) => th.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath || (m.url as string),
      }));
    onConfirm(selected);
  };

  const handleAudioSelect = (file: any) => {
    const audioFile = {
      id: file.fileId as string,
      url: file.url as string,
      isAudio: true,
      thumbnail: file.url as string,
    };
    onConfirm([audioFile]);
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();

  const handleOverlayClick = () => onClose?.();
  const handleModalClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => e.stopPropagation();

  const modalContent = (
    <>
      <div className="select-quick-file-media-overlay" onClick={handleOverlayClick}>
        <div className="select-quick-file-media-content" onClick={handleModalClick}>
          <div className="modal-header-line-wrapper">
            <div className="modal-header-line"></div>
          </div>
          <div className="select-quick-file-media-container">
            <img src={ChevronBackIcon} alt="back" className="select-quick-file-media-back-icon" onClick={onBack} />
            <div className="select-quick-file-media-title">{type === "audio" ? t("select-audio") : t("select-media")}</div>
            <div className="select-quick-file-media-body">
              {loading ? (
                <div className="select-quick-file-media-loader">
                  <Loader />
                </div>
              ) : type === "audio" ? (
                <div className="select-quick-file-audio-list">
                  {media?.map((m: any) => {
                    const id = m.fileId as string;
                    return (
                      <div key={id} className="select-quick-file-audio-item">
                        <VoiceChatListRow
                          file={m}
                          title={m.title || "Audio"}
                          date={formatDate(m.createdAt)}
                          duration={m.time || 0}
                          select={true}
                          onSelectAndSend={() => handleAudioSelect(m)}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="select-quick-file-media-grid">
                  {media?.map((m: any) => {
                    const id = m.fileId as string;
                    const thumb =
                      (m.thumbnails as IThumbnail[] | undefined)?.find((th) => th.type === THUMBNAIL_TYPES.CLEAR)?.absolutePath || (m.url as string);
                    const selected = selectedIds.has(id);
                    const isVideo =
                      (m.mimeType as string)?.startsWith("video/") ||
                      (m.url as string)?.includes(".mp4") ||
                      (m.url as string)?.includes(".webm") ||
                      (m.url as string)?.includes(".mov");
                    const duration = isVideo ? videoDurations[id] : undefined;
                    return (
                      <div key={id} className={`select-quick-file-media-item ${selected ? "selected" : ""}`}>
                        <div
                          className="selection-circle"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelect(id);
                          }}
                        >
                          <img src={selected ? SelectedIcon : UnselectIcon} alt="select" />
                        </div>

                        <div
                          className="select-quick-file-media-thumb"
                          style={{ backgroundImage: `url(${thumb || FALLBACK_IMAGE})` }}
                          onClick={() => {
                            setPreviewUrl(m.url as string);
                            setIsPreviewVideo(isVideo);
                            setPreviewVisible(true);
                          }}
                        >
                          {isVideo && (
                            <div className="video-indicator">
                              <img src={PlayIcon} alt="play" />
                            </div>
                          )}
                          {duration && <span className="video-duration">{duration}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {type !== "audio" && (
              <div className="select-quick-file-media-actions">
                <button type="button" className="select-quick-file-media-cancel-button" onClick={onClose}>
                  {t("cancel")}
                </button>
                <button type="button" className="select-quick-file-media-confirm-button" onClick={handleConfirm} disabled={selectedIds.size === 0}>
                  {t("select-er")} ({selectedIds.size})
                </button>
              </div>
            )}
            {type === "audio" && (
              <div className="select-quick-file-media-actions">
                <button type="button" className="select-quick-file-media-cancel-button" onClick={onClose}>
                  {t("cancel")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}

      {previewVisible &&
        !isPreviewVideo &&
        createPortal(
          <div className="select-quick-file-image-preview-overlay" onClick={() => setPreviewVisible(false)}>
            <img src={CloseIcon} alt="close" className="select-quick-file-preview-close-icon" onClick={() => setPreviewVisible(false)} />
            <div className="select-quick-file-image-preview-container" onClick={(e) => e.stopPropagation()}>
              <img src={previewUrl} alt="preview" className="select-quick-file-preview-image" />
            </div>
          </div>,
          document.body
        )}

      {previewVisible &&
        isPreviewVideo &&
        createPortal(
          <div className="select-quick-file-video-preview-overlay" onClick={() => setPreviewVisible(false)}>
            <img src={CloseIcon} alt="close" className="select-quick-file-preview-close-icon" onClick={() => setPreviewVisible(false)} />
            <div className="select-quick-file-video-preview-container" onClick={(e) => e.stopPropagation()}>
              <VideoPlayer videoUrl={previewUrl} thumbnailUrl={""} autoPlay={true} />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SelectQuickFileMediaModal;
